import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, User, Mail, Phone, Calendar, Globe, MessageSquare, Save, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import CVPreview from '../CVPreview';
import { applicationsAPI, handleApiError } from '../../utils/api';

const NOTES_PAGE_SIZE = 5;

const ApplicationModal = ({ application, onClose, onStatusUpdate, onPreviewCV, onManageCV }) => {
  const { t, currentLanguage } = useLanguage();
  const [notes, setNotes] = useState([]);
  const [notesPage, setNotesPage] = useState(1);
  const [notesHasMore, setNotesHasMore] = useState(false);
  const [notesRemaining, setNotesRemaining] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [showCvPreview, setShowCvPreview] = useState(false);
  const [cvPreviewData, setCvPreviewData] = useState(null);
  const [addingToKanban, setAddingToKanban] = useState(false);
  const [isInKanban, setIsInKanban] = useState(false);

  const loadNotes = useCallback(async (page = 1, append = false) => {
    if (!application?.id) return;
    setNotesLoading(true);
    try {
      const response = await applicationsAPI.getNotes(application.id, { page, limit: NOTES_PAGE_SIZE });
      const data = response.data;
      const incomingNotes = data.notes || [];
      setNotes((prev) => (append ? [...prev, ...incomingNotes] : incomingNotes));
      const pagination = data.pagination || {};
      const total = typeof pagination.total === 'number' ? pagination.total : null;
      const limit = typeof pagination.limit === 'number' ? pagination.limit : NOTES_PAGE_SIZE;
      const current = typeof pagination.page === 'number' ? pagination.page : page;
      const remaining = total !== null ? Math.max(total - current * limit, 0) : null;
      setNotesPage(current);
      setNotesHasMore(!!pagination.hasMore);
      setNotesRemaining(remaining);
    } catch (error) {
      toast.error(handleApiError(error, t('dashboard.applications.messages.noteLoadError')));
    } finally {
      setNotesLoading(false);
    }
  }, [application?.id, t]);

  useEffect(() => {
    if (application?.id) {
      setNotes([]);
      setNotesPage(1);
      setNotesHasMore(false);
      setNotesRemaining(null);
      setNoteInput('');
      setEditingNoteId(null);
      setEditingContent('');
      setIsInKanban(false);
      loadNotes(1, false);
    }
  }, [application?.id, loadNotes]);

  const handleAddToKanban = async () => {
    if (!application?.id || addingToKanban || isInKanban) return;
    setAddingToKanban(true);
    try {
      await applicationsAPI.addToKanban(application.id);
      setIsInKanban(true);
      toast.success(t('dashboard.applications.messages.addedToKanban'));
    } catch (error) {
      const code = error?.response?.data?.code;
      if (code === 'APPLICATION_ALREADY_IN_KANBAN') {
        setIsInKanban(true);
        toast.info(t('dashboard.applications.messages.alreadyInKanban'));
        return;
      }
      if (code === 'AI_CONFIG_MISSING') {
        toast.error(t('dashboard.applications.messages.aiConfigMissing'));
        return;
      }
      if (code === 'AI_INVALID_TOKEN') {
        toast.error(t('dashboard.applications.messages.aiInvalidToken'));
        return;
      }
      toast.error(handleApiError(error, t('dashboard.applications.messages.addToKanbanError')));
    } finally {
      setAddingToKanban(false);
    }
  };

  const handleAddNote = async () => {
    const content = noteInput.trim();
    if (!content || noteSaving || !application?.id) return;
    setNoteSaving(true);
    try {
      const response = await applicationsAPI.createNote(application.id, { content });
      const createdNote = response.data.note;
      setNotes((prev) => [createdNote, ...prev]);
      setNoteInput('');
      toast.success(t('dashboard.applications.messages.noteAdded'));
    } catch (error) {
      toast.error(handleApiError(error, t('dashboard.applications.messages.noteAddError')));
    } finally {
      setNoteSaving(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content || '');
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  const handleSaveEdit = async (noteId) => {
    const content = editingContent.trim();
    if (!content) return;
    try {
      const response = await applicationsAPI.updateNote(application.id, noteId, { content });
      const updatedNote = response.data.note;
      setNotes((prev) => prev.map((note) => (note.id === noteId ? updatedNote : note)));
      setEditingNoteId(null);
      setEditingContent('');
      toast.success(t('dashboard.applications.messages.noteUpdated'));
    } catch (error) {
      toast.error(handleApiError(error, t('dashboard.applications.messages.noteUpdateError')));
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await applicationsAPI.deleteNote(application.id, noteId);
      setNotes((prev) => prev.filter((note) => note.id !== noteId));
      toast.success(t('dashboard.applications.messages.noteDeleted'));
    } catch (error) {
      toast.error(handleApiError(error, t('dashboard.applications.messages.noteDeleteError')));
    }
  };

  const handlePreviewCvFromModal = async () => {
    if (!application?.cv_id) return;
    if (onPreviewCV) {
      const cvData = await onPreviewCV();
      if (cvData) {
        setCvPreviewData(cvData);
        setShowCvPreview(true);
      }
      return;
    }
  };

  if (!application) return null;

  const applicantName = `${application.first_name || ''} ${application.last_name || ''}`.trim() || application.candidate_name;
  const applicantEmail = application.candidate_email || application.email;
  const applicantPhone = application.candidate_phone || application.phone;
  const appliedDate = application.created_at ? new Date(application.created_at).toLocaleDateString(
    currentLanguage === 'fr' ? 'fr-FR' : currentLanguage === 'es' ? 'es-ES' : 'en-US'
  ) : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.98, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 10 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 truncate">
              {applicantName || t('dashboard.applications.modal.candidateInfo')}
            </h2>
            <p className="text-sm text-gray-500 truncate">
              {application.offer_title || application.position || ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 max-h-[calc(90vh-70px)] overflow-hidden">
          {/* Left panel */}
          <div className="lg:col-span-2 p-6 overflow-y-auto border-r border-gray-100">
            <div className="space-y-6">
              {/* Candidate info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {t('dashboard.applications.modal.candidateInfo')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2 text-gray-400" />
                    {applicantName}
                  </div>
                  {applicantEmail && (
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      {applicantEmail}
                    </div>
                  )}
                  {applicantPhone && (
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      {applicantPhone}
                    </div>
                  )}
                  {appliedDate && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {t('dashboard.applications.modal.appliedOn', { date: appliedDate })}
                    </div>
                  )}
                  {application.source && (
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 mr-2 text-gray-400" />
                      {t('dashboard.applications.modal.source', { source: application.source })}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onStatusUpdate(application.id, 'reviewed')}
                  className="px-3 py-2 text-sm bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
                >
                  {t('dashboard.applications.actions.markAsReviewed')}
                </button>
                <button
                  onClick={() => onStatusUpdate(application.id, 'shortlisted')}
                  className="px-3 py-2 text-sm bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors"
                >
                  {t('dashboard.applications.actions.shortlist')}
                </button>
                <button
                  onClick={() => onStatusUpdate(application.id, 'rejected')}
                  className="px-3 py-2 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                >
                  {t('dashboard.applications.actions.reject')}
                </button>
                <button
                  onClick={handleAddToKanban}
                  disabled={addingToKanban || isInKanban}
                  className="px-3 py-2 text-sm bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {addingToKanban
                    ? t('common.loading')
                    : isInKanban
                      ? t('dashboard.applications.actions.alreadyInKanban')
                      : t('dashboard.applications.actions.addToKanban')}
                </button>
                {application.cv_id && (
                  <>
                    <button
                      onClick={handlePreviewCvFromModal}
                      className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      {t('dashboard.applications.actions.previewCV')}
                    </button>
                    {onManageCV && (
                      <button
                        onClick={() => {
                          onManageCV();
                          onClose();
                        }}
                        className="px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        {t('dashboard.applications.actions.manageCV')}
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Candidate message */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  {t('dashboard.applications.modal.candidateMessage')}
                </h3>
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-line">
                  {application.message || '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Right panel - Notes */}
          <div className="p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                {t('dashboard.applications.modal.internalNotes')}
              </h3>
            </div>

            {/* Add note */}
            <div className="mb-4">
              <textarea
                rows={3}
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                placeholder={t('dashboard.applications.modal.addNote')}
                className="w-full text-sm border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleAddNote}
                  disabled={noteSaving || !noteInput.trim()}
                  className="inline-flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {t('dashboard.applications.modal.addNoteButton')}
                </button>
              </div>
            </div>

            {/* Notes list */}
            <div className="space-y-3">
              {notes.length === 0 && !notesLoading && (
                <div className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4">
                  <p>{t('dashboard.applications.modal.noNotes')}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('dashboard.applications.modal.noNotesHint')}</p>
                </div>
              )}

              {notes.map((note) => {
                const isEditing = editingNoteId === note.id;
                const updated = note.updated_at && note.updated_at !== note.created_at;
                return (
                  <div key={note.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="text-xs text-gray-500">
                        <div className="flex items-center">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          <span className="font-medium text-gray-700">{note.author_name}</span>
                          {updated && (
                            <span className="ml-2 text-gray-400">{t('dashboard.applications.modal.modified')}</span>
                          )}
                        </div>
                        <div>{new Date(note.created_at).toLocaleString(currentLanguage === 'fr' ? 'fr-FR' : currentLanguage === 'es' ? 'es-ES' : 'en-US')}</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {!isEditing && (
                          <button
                            onClick={() => handleEditNote(note)}
                            className="text-gray-400 hover:text-gray-600"
                            title={t('dashboard.applications.modal.edit')}
                            aria-label={t('dashboard.applications.modal.edit')}
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-gray-400 hover:text-red-600"
                          title={t('common.delete')}
                          aria-label={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {isEditing ? (
                      <div>
                        <textarea
                          rows={3}
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            onClick={() => handleSaveEdit(note.id)}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            {t('dashboard.applications.modal.save')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 whitespace-pre-line">{note.content}</p>
                    )}
                  </div>
                );
              })}
            </div>

            {notesHasMore && (
              <button
                onClick={() => loadNotes(notesPage + 1, true)}
                disabled={notesLoading}
                className="mt-4 w-full text-sm text-blue-600 hover:text-blue-700"
              >
                {t('dashboard.applications.modal.loadMoreNotes', { count: notesRemaining ?? NOTES_PAGE_SIZE })}
              </button>
            )}
          </div>
        </div>
      </motion.div>

      <CVPreview
        cv={cvPreviewData}
        isOpen={showCvPreview}
        onClose={() => {
          setShowCvPreview(false);
          setCvPreviewData(null);
        }}
      />
    </div>
  );
};

export default ApplicationModal;
